import { ref } from "vue";
import CryptoJS from "crypto-js";
import { uploadQiniu } from "@/api/user";

export function useUploadStorage(options = {}) {
  const uploadDirectory = String(options.directory || "")
    .trim()
    .replace(/\/+$/g, "");
  const useGeneratedObjectPath = options.useGeneratedObjectPath === true;
  const transformUploadedKey =
    typeof options.transformUploadedKey === "function" ? options.transformUploadedKey : (key) => key;
  const uploadFileList = ref([]);
  const isUploading = ref(false);
  const uploadProgress = ref(0);

  // 照片计算MD5 
  function calculateMD5(fileContent) {
    const md5 = CryptoJS.MD5(CryptoJS.lib.WordArray.create(fileContent)).toString();
    return md5;
  }

  // 获取文件扩展名的安全方法
  function getFileExtension(file) {
    // 优先从文件名获取扩展名
    if (file.name && file.name.includes('.')) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (ext) return ext;
    }
    
    // 从MIME类型获取扩展名
    if (file.type && file.type.includes('/')) {
      const mimeExt = file.type.split('/')[1];
      if (mimeExt && mimeExt !== 'undefined') {
        // 处理特殊的MIME类型
        const mimeMap = {
          'jpeg': 'jpg',
          'svg+xml': 'svg'
        };
        return mimeMap[mimeExt] || mimeExt;
      }
    }
    
    // 默认扩展名
    return 'jpg';
  }

  // 图片校验和MD5文件名生成
  function beforePicUpload(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const md5 = calculateMD5(e.target.result);
        const extension = getFileExtension(file);
        let name = `${md5}.${extension}`;
        file.md5name = name;
        resolve(file);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsArrayBuffer(file);
    })
      .then((file) => {
        return file;
      })
      .catch((error) => {
        console.error(error);
        throw error;
      });
  }

  async function onFileChange(file, fileList) {
    uploadFileList.value = fileList;
  }

  function reset() {
    uploadFileList.value = [];
    isUploading.value = false;
    uploadProgress.value = 0;
  }

  async function uploadAllSelected() {
    const results = [];
    const files = uploadFileList.value.slice();
    if (files.length === 0) return results;

    isUploading.value = true;
    uploadProgress.value = 0;

    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const actualFile = f.raw || f;
      
      try {
        // 先进行MD5计算和文件校验 
        const processedFile = await beforePicUpload(actualFile);
        
        // 创建FormData，使用MD5重命名的文件
        const formData = new FormData();
        const renamedFile = new File([processedFile], processedFile.md5name, {
          type: processedFile.type
        });
        formData.append("file", renamedFile); 
        formData.append("name", processedFile.md5name);
        if (uploadDirectory) {
          formData.append("dir", uploadDirectory);
        }
        
        // 将MD5信息保存到原文件对象中
        f.md5name = processedFile.md5name;
        
        const res = await uploadQiniu(formData);
        if (!res || res.code !== 0) {
          throw new Error(res?.message || "图片上传失败");
        }

        const generatedObjectPath = [uploadDirectory, processedFile.md5name].filter(Boolean).join("/");
        const responseKey = (res.data && res.data.key) || (res.data && res.data.url) || res.data;
        const objectKey =
          !useGeneratedObjectPath && typeof responseKey === "string" && responseKey.trim()
            ? responseKey.trim()
            : generatedObjectPath;

        results.push(transformUploadedKey(objectKey));
      } catch (error) {
        console.error('文件上传失败:', error);
        isUploading.value = false;
        throw error;
      }
      
      uploadProgress.value = Math.round(((i + 1) / files.length) * 100);
    }

    isUploading.value = false;
    return results;
  }

  return {
    uploadFileList,
    isUploading,
    uploadProgress,
    onFileChange,
    uploadAllSelected,
    reset,
  };
}

